<template>
  <div class="calendar-admin-config">
    <!-- Page header -->
    <div class="calendar-admin-config__header">
      <theme-icon icon="calendar" type="icons" classes="w-6 h-6" />
      <h1 class="calendar-admin-config__title">Calendar Configuration</h1>
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="calendar-admin-config__success">
      {{ successMessage }}
    </div>

    <!-- General error -->
    <div v-if="errors._general" class="calendar-admin-config__error">
      {{ errors._general }}
    </div>

    <!-- All cards on same row -->
    <div class="calendar-admin-config__cards-row">
    <!-- General card -->
    <themed-card class="calendar-admin-config__card">
      <template #header>
        <h2 class="calendar-admin-config__section-title">General</h2>
        <p class="calendar-admin-config__section-desc">Control overall calendar sync visibility.</p>
      </template>

      <div class="calendar-admin-config__toggles">
        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Show Calendar Sync Tab</span>
            <span class="calendar-admin-config__toggle-hint">When disabled, the Calendar Sync tab is hidden from all users.</span>
            <span v-if="errors['general.show_tab']" class="calendar-admin-config__field-error">
              {{ errors['general.show_tab'][0] }}
            </span>
          </div>
          <themed-toggle
            id="general-show-tab"
            :value="localConfig.general.show_tab"
            @input="localConfig.general.show_tab = $event"
          />
        </div>
      </div>
    </themed-card>

    <!-- Providers card -->
    <themed-card class="calendar-admin-config__card">
      <template #header>
        <h2 class="calendar-admin-config__section-title">Calendar Providers</h2>
        <p class="calendar-admin-config__section-desc">Control which calendar integrations are available to users.</p>
      </template>

      <div class="calendar-admin-config__toggles">
        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Google Calendar</span>
            <span v-if="errors['providers.google']" class="calendar-admin-config__field-error">
              {{ errors['providers.google'][0] }}
            </span>
          </div>
          <themed-toggle
            id="provider-google"
            :value="localConfig.providers.google"
            @input="localConfig.providers.google = $event"
          />
        </div>

        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Outlook</span>
            <span v-if="errors['providers.outlook']" class="calendar-admin-config__field-error">
              {{ errors['providers.outlook'][0] }}
            </span>
          </div>
          <themed-toggle
            id="provider-outlook"
            :value="localConfig.providers.outlook"
            @input="localConfig.providers.outlook = $event"
          />
        </div>

        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">iCal</span>
            <span v-if="errors['providers.ical']" class="calendar-admin-config__field-error">
              {{ errors['providers.ical'][0] }}
            </span>
          </div>
          <themed-toggle
            id="provider-ical"
            :value="localConfig.providers.ical"
            @input="localConfig.providers.ical = $event"
          />
        </div>
      </div>
    </themed-card>

    <!-- Event Types card -->
    <themed-card class="calendar-admin-config__card">
      <template #header>
        <h2 class="calendar-admin-config__section-title">Event Types</h2>
        <p class="calendar-admin-config__section-desc">Control which event categories users can sync to their calendars.</p>
      </template>

      <div class="calendar-admin-config__toggles">
        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Tasks</span>
            <span v-if="errors['event_types.tasks']" class="calendar-admin-config__field-error">
              {{ errors['event_types.tasks'][0] }}
            </span>
          </div>
          <themed-toggle
            id="event-type-tasks"
            :value="localConfig.event_types.tasks"
            @input="localConfig.event_types.tasks = $event"
          />
        </div>

        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Tours</span>
            <span v-if="errors['event_types.tours']" class="calendar-admin-config__field-error">
              {{ errors['event_types.tours'][0] }}
            </span>
          </div>
          <themed-toggle
            id="event-type-tours"
            :value="localConfig.event_types.tours"
            @input="localConfig.event_types.tours = $event"
          />
        </div>

        <div class="calendar-admin-config__toggle-row">
          <div class="calendar-admin-config__toggle-info">
            <span class="calendar-admin-config__toggle-label">Key Dates</span>
            <span v-if="errors['event_types.key_dates']" class="calendar-admin-config__field-error">
              {{ errors['event_types.key_dates'][0] }}
            </span>
          </div>
          <themed-toggle
            id="event-type-key-dates"
            :value="localConfig.event_types.key_dates"
            @input="localConfig.event_types.key_dates = $event"
          />
        </div>
      </div>
    </themed-card>
    </div><!-- end cards-row -->

    <!-- Save button -->
    <div class="calendar-admin-config__actions">
      <themed-button
        variant="default"
        type="submit"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? 'Saving...' : 'Save Configuration' }}
      </themed-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalendarAdminConfig',

  props: {
    config: {
      type: Object,
      default: () => ({
        general: { show_tab: true },
        providers: { google: true, outlook: true },
        event_types: { tasks: true, tours: true, key_dates: true },
      }),
    },
    saveUrl: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      localConfig: {
        general: { show_tab: true },
        providers: { google: true, outlook: true, ical: true },
        event_types: { tasks: true, tours: true, key_dates: true },
      },
      saving: false,
      errors: {},
      successMessage: '',
    };
  },

  created() {
    if (this.config) {
      this.localConfig = {
        general: {
          show_tab: this.config.general ? !!this.config.general.show_tab : true,
        },
        providers: {
          google: this.config.providers ? !!this.config.providers.google : true,
          outlook: this.config.providers ? !!this.config.providers.outlook : true,
          ical: this.config.providers ? !!this.config.providers.ical : true,
        },
        event_types: {
          tasks: this.config.event_types ? !!this.config.event_types.tasks : true,
          tours: this.config.event_types ? !!this.config.event_types.tours : true,
          key_dates: this.config.event_types ? !!this.config.event_types.key_dates : true,
        },
      };
    }

    if (window.flashSuccess) {
      this.successMessage = window.flashSuccess;
    }
  },

  watch: {
    'localConfig.providers': {
      deep: true,
      handler(providers) {
        const anyEnabled = providers.google || providers.outlook || providers.ical;
        const allDisabled = !providers.google && !providers.outlook && !providers.ical;
        if (allDisabled) {
          this.localConfig.general.show_tab = false;
        } else if (anyEnabled) {
          this.localConfig.general.show_tab = true;
        }
      },
    },
    'localConfig.event_types': {
      deep: true,
      handler(types) {
        const anyEnabled = types.tasks || types.tours || types.key_dates;
        const allDisabled = !types.tasks && !types.tours && !types.key_dates;
        if (allDisabled) {
          this.localConfig.general.show_tab = false;
        } else if (anyEnabled) {
          this.localConfig.general.show_tab = true;
        }
      },
    },
  },

  methods: {
    async save() {
      this.saving = true;
      this.errors = {};
      this.successMessage = '';

      try {
        const response = await window.axios.post(this.saveUrl, {
          general: this.localConfig.general,
          providers: this.localConfig.providers,
          event_types: this.localConfig.event_types,
        });

        this.successMessage = response.data.message || 'Configuration saved successfully.';
        this.$notify({
          type: 'success',
          title: 'Saved',
          text: this.successMessage,
        });
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        } else {
          const message = error.response?.data?.message || 'An error occurred while saving. Please try again.';
          this.errors = { _general: message };
          this.$notify({
            type: 'error',
            title: 'Error',
            text: message,
          });
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
